import { Component, inject, OnInit, OnDestroy, signal, ViewChild, ChangeDetectorRef, effect } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, takeUntil, firstValueFrom } from 'rxjs';
import { AssessmentsService } from '../../service/entity-services/assessments.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';
import { EntityActionsSidenavComponent, EntityAction } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { Assessment } from '../../objects/assessment';

/** Read-only view of one assessment, laid out like the legacy SRH detail page. */
@Component({
  selector: 'app-assessment-item',
  templateUrl: './assessment-item.component.html',
  styleUrls: ['./assessment-item.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatChipsModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatDialogModule,
    MatSnackBarModule,
    DatePipe,
    EntityActionsSidenavComponent
  ]
})
export class AssessmentItemComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private assessmentsService = inject(AssessmentsService);
  private sidenavContentService = inject(SidenavContentService);
  private permissionsStore = inject(PermissionsStoreService);
  private destroy$ = new Subject<void>();

  @ViewChild('actionsSidenav') actionsSidenav!: MatSidenav;

  assessment: Assessment | null = null;
  /** Other assessments on the same record (legacy "Assessment History"). */
  history: Assessment[] = [];
  instructionsHtml = '';
  attestationText = '';
  loading = true;
  deleting = false;
  error: string | null = null;

  private readonly isViewer = this.permissionsStore.isViewer;
  private readonly isAdmin = this.permissionsStore.isAdmin;

  /** Edit for Editors and up; Delete for Administrators, v2 orgs only (the API enforces both). */
  sidenavActions = signal<EntityAction[]>([]);

  constructor() {
    // Roles can load after the page does; keep the actions in step.
    effect(() => this.sidenavActions.set(this.buildActions()));
  }

  private buildActions(): EntityAction[] {
    const actions: EntityAction[] = [];
    if (!this.isViewer()) {
      actions.push({ label: 'Edit', icon: 'edit', action: () => this.editAssessment(), disabled: false });
    }
    if (this.isAdmin() && this.assessmentsService.usesV2) {
      actions.push({ label: 'Delete', icon: 'delete', action: () => this.deleteAssessment(), disabled: false });
    }
    actions.push({ label: 'Back to List', icon: 'list', action: () => this.router.navigate(['/securehome/assessments']), disabled: false });
    return actions;
  }

  ngOnInit(): void {
    this.sidenavContentService.registerDetailSidenavToggle(() => {
      if (this.actionsSidenav) this.actionsSidenav.toggle();
    });

    this.assessmentsService.getEvaluationInstructions().subscribe({ next: html => this.instructionsHtml = html, error: () => {} });
    this.assessmentsService.getAttestation().subscribe({ next: a => this.attestationText = a?.[0] || '', error: () => {} });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) this.loadAssessment(params['id']);
    });
  }

  ngOnDestroy(): void {
    this.sidenavContentService.unregisterDetailSidenavToggle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadAssessment(assessmentNum: string): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      this.assessment = this.assessmentsService.usesV2
        ? await firstValueFrom(this.assessmentsService.getAssessment(assessmentNum))
        : await firstValueFrom(this.assessmentsService.getAssessmentByNum(assessmentNum));
      if (!this.assessment) {
        this.error = 'Assessment not found';
      } else if (this.assessment.entityId) {
        this.assessmentsService.getAssessmentsByEntityId(this.assessment.entityId).subscribe({
          next: list => {
            this.history = list.filter(a => a.assessmentNum !== assessmentNum);
            this.cdr.detectChanges();
          },
          error: () => this.history = []
        });
      }
    } catch (err) {
      this.error = this.assessmentsService.describeError(err);
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  getPersonName(person: any): string {
    if (!person) return '—';
    if (typeof person === 'string') return person;
    return person.shortFriendlyName || person.friendlyName || person.email || '—';
  }

  /** "3 - Very Good" when stored; the bare number otherwise. */
  scoreText(qa: { score: number | string; scoreDescription?: string }): string {
    return qa.scoreDescription || String(qa.score ?? '—');
  }

  get totalScore(): number {
    return this.assessment?.totalScore ?? this.assessmentsService.sumScores(this.assessment?.questionsAndAnswers);
  }

  editAssessment(): void {
    if (this.assessment?.assessmentNum) {
      this.router.navigate(['/securehome/new-assessment', this.assessment.assessmentNum]);
    }
  }

  openAssessment(a: Assessment): void {
    this.router.navigate(['/securehome/assessment', a.assessmentNum]);
  }

  private deleteAssessment(): void {
    const a = this.assessment;
    if (!a?.assessmentNum || this.deleting) return;
    const data: ConfirmDialogData = {
      title: 'Delete assessment?',
      message: `"${a.name || 'This assessment'}" will be permanently deleted. This can't be undone from the app.`,
      confirmLabel: 'Delete',
      icon: 'delete',
      warn: true,
      typeToConfirm: 'delete'
    };
    this.dialog.open(ConfirmDialogComponent, { data, width: '440px' }).afterClosed().subscribe(async ok => {
      if (!ok) return;
      this.deleting = true;
      try {
        await firstValueFrom(this.assessmentsService.deleteAssessment(a.assessmentNum as string));
        this.snackBar.open('Assessment deleted', 'Close', { duration: 3000 });
        this.router.navigate(['/securehome/assessments']);
      } catch (err) {
        this.snackBar.open(this.assessmentsService.describeError(err), 'Close', { duration: 6000 });
      } finally {
        this.deleting = false;
      }
    });
  }

  goBack(): void {
    window.history.back();
  }
}
